import { useEffect } from 'react';

// Smooth scroll-triggered reveal: adds `.in-view` to any `.reveal` element
// once it scrolls into view. Handles dynamically added elements via MutationObserver.
function useReveal() {
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      // Fallback: reveal everything so content is never left hidden.
      document.querySelectorAll('.reveal').forEach((el) => el.classList.add('in-view'));
      return undefined;
    }

    const obs = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    // Observe all existing .reveal elements
    const observeElements = () => {
      const els = Array.from(document.querySelectorAll('.reveal:not(.in-view)'));
      els.forEach((el) => obs.observe(el));
    };

    observeElements();

    // Watch for new .reveal elements being added to the DOM
    const mutationObs = new MutationObserver(() => {
      observeElements();
    });

    mutationObs.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      obs.disconnect();
      mutationObs.disconnect();
    };
  }, []);
}

export { useReveal };
